import React, { useState, useEffect } from 'react';
import { useAuth } from '../utils/AuthContext';
import { useStore } from '../utils/StoreContext';
import { Package, BarChart3, Users, TrendingUp, RefreshCw } from 'lucide-react';
import MovementTrackerWidget from '../components/MovementTrackerWidget';
import MovementCategoryModal from '../components/MovementCategoryModal';
import DailyMovementTracker from '../components/DailyMovementTracker';
import BulkPriceSyncModal from '../components/BulkPriceSyncModal';
import { AnalyticsService, DailyMovementAnalytics } from '../services/analyticsService';

const Dashboard: React.FC = () => {
  const { user } = useAuth();
  const { currentStore } = useStore();
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
  const [showCategoryModal, setShowCategoryModal] = useState(false);
  const [dashboardMetrics, setDashboardMetrics] = useState<DailyMovementAnalytics | null>(null);
  const [metricsLoading, setMetricsLoading] = useState(true);
  const [showBulkPriceSyncModal, setShowBulkPriceSyncModal] = useState(false);

  // Handle category click from movement tracker
  const handleCategoryClick = (category: string) => {
    setSelectedCategory(category);
    setShowCategoryModal(true);
  };

  // Close category modal
  const handleCloseCategoryModal = () => {
    setShowCategoryModal(false);
    setSelectedCategory(null);
  };

  // Load dashboard metrics
  const loadDashboardMetrics = async () => {
    if (!user?.id || !currentStore?.id) return;

    try {
      setMetricsLoading(true);
      
      const data = await AnalyticsService.getDailyMovementAnalytics(
        user.id,
        currentStore.id,
        undefined, // Use default start date
        undefined, // Use default end date
        1 // Just get today's data for metrics
      );
      
      setDashboardMetrics(data);
    } catch (err) {
      console.error('Error loading dashboard metrics:', err);
    } finally {
      setMetricsLoading(false);
    }
  };

  // Load metrics on component mount and when dependencies change
  useEffect(() => {
    loadDashboardMetrics();
  }, [user?.id, currentStore?.id]);

  return (
    <div className="space-y-6">
      {/* Welcome Section */}
      <div className="bg-white rounded-lg shadow-sm p-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">
              Welcome back, {user?.name || 'Admin'}!
            </h1>
            <p className="text-gray-600 mt-1">
              {currentStore?.name} - Stock Management Dashboard
            </p>
          </div>
          <div className="flex items-center gap-4">
            {user?.user_type === 'Admin' && (
              <button
                onClick={() => setShowBulkPriceSyncModal(true)}
                className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg flex items-center gap-2 transition-colors"
              >
                <RefreshCw className="w-4 h-4" />
                Sync All Items
              </button>
            )}
            <div className="text-right">
              <p className="text-sm text-gray-500">Current Store</p>
              <p className="font-semibold text-gray-900">{currentStore?.name}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Quick Stats Cards - Only show for Admin users */}
      {dashboardMetrics?.summary.is_admin && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          <div className="bg-white rounded-lg shadow-sm p-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center">
                <div className="p-2 bg-blue-100 rounded-lg">
                  <Package className="w-6 h-6 text-blue-600" />
                </div>
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-600">Total Items</p>
                  <p className="text-2xl font-bold text-gray-900">
                    {metricsLoading ? (
                      <RefreshCw className="w-6 h-6 animate-spin" />
                    ) : (
                      dashboardMetrics?.summary.total_items ?? '-'
                    )}
                  </p>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-sm p-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center">
                <div className="p-2 bg-green-100 rounded-lg">
                  <TrendingUp className="w-6 h-6 text-green-600" />
                </div>
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-600">Stock Value</p>
                  <p className="text-2xl font-bold text-gray-900">
                    {metricsLoading ? (
                      <RefreshCw className="w-6 h-6 animate-spin" />
                    ) : (
                      dashboardMetrics?.summary.total_stock_value ? 
                        `MVR ${dashboardMetrics.summary.total_stock_value.toLocaleString()}` :
                        '-'
                    )}
                  </p>
                </div>
              </div>
            </div>
          </div>
                        
          <div className="bg-white rounded-lg shadow-sm p-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center">
                <div className="p-2 bg-purple-100 rounded-lg">
                  <BarChart3 className="w-6 h-6 text-purple-600" />
                </div>
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-600">Movements Today</p>
                  <p className="text-2xl font-bold text-gray-900">
                    {metricsLoading ? (
                      <RefreshCw className="w-6 h-6 animate-spin" />
                    ) : (
                      dashboardMetrics?.summary.total_movements_today || 0
                    )}
                  </p>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-sm p-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center">
                <div className="p-2 bg-orange-100 rounded-lg">
                  <Users className="w-6 h-6 text-orange-600" />
                </div>
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-600">Active Users</p>
                  <p className="text-2xl font-bold text-gray-900">
                    {metricsLoading ? (
                      <RefreshCw className="w-6 h-6 animate-spin" />
                    ) : (
                      dashboardMetrics?.summary.active_users_today || 0
                    )}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

            {/* Analytics Widgets */}
      <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
        {/* Movement Tracker Widget */}
        <MovementTrackerWidget onCategoryClick={handleCategoryClick} />
        
        {/* Daily Movement Tracker */}
        <DailyMovementTracker />
      </div>

      {/* Category Modal */}
      {showCategoryModal && selectedCategory && (
        <MovementCategoryModal
          isOpen={showCategoryModal}
          onClose={handleCloseCategoryModal}
          category={selectedCategory as 'no_movement' | 'slow_moving' | 'medium_moving' | 'fast_moving'}
        />
      )}

      {/* Bulk Price Sync Modal */}
      {showBulkPriceSyncModal && (
        <BulkPriceSyncModal
          onClose={() => setShowBulkPriceSyncModal(false)}
          onSyncComplete={() => {
            // Optionally reload metrics after sync
            loadDashboardMetrics();
          }}
        />
      )}
    </div>
  );
};

export default Dashboard; 
